import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LiveSurfReport from "@/components/LiveSurfReport";
import ShareButton from "@/components/ShareButton";
import BackToTopButton from "@/components/BackToTopButton";
import { useAlertCount } from "@/components/AlertsPanel";
import { useI18n } from "@/contexts/I18nContext";
import { http } from "@/lib/api";
import { useSEO, PUBLIC_BASE } from "@/hooks/useSEO";
import { useJsonLd } from "@/hooks/useJsonLd";
import { scrollToPageTop } from "@/lib/scroll";
import { Radio, MapPin, Clock, Info, Waves, ArrowRight, VideoOff } from "lucide-react";

const PATH = "/live-surf-cam";

/** Extract a YouTube video id from any common link shape. */
function ytId(url) {
  try {
    const u = new URL(url);
    let id = "";
    if (u.hostname.includes("youtu.be")) id = u.pathname.slice(1);
    else if (u.pathname.startsWith("/watch")) id = u.searchParams.get("v") || "";
    else if (u.pathname.startsWith("/live/")) id = u.pathname.split("/live/")[1];
    else if (u.pathname.startsWith("/embed/")) id = u.pathname.split("/embed/")[1];
    id = (id || "").split("/")[0].split("?")[0];
    return id || null;
  } catch (_e) {
    return null;
  }
}

export default function LiveSurf() {
  const { t, lang } = useI18n();
  const alertCount = useAlertCount();
  const [config, setConfig] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { scrollToPageTop(); }, []);

  useEffect(() => {
    let alive = true;
    http.get("/live-surf")
      .then((r) => { if (alive) setConfig(r.data); })
      .catch(() => { if (alive) setConfig({ mode: "none" }); })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  const isLive = config && config.mode && config.mode !== "none";
  const ytVideoId = isLive && config?.url ? ytId(config.url) : null;

  useSEO({
    title: `${t("livecam_h1")} — ${t("livecam_tagline")} · Popoyo.app`,
    description: t("livecam_intro"),
    image: ytVideoId ? `https://i.ytimg.com/vi/${ytVideoId}/maxresdefault.jpg` : undefined,
    path: PATH,
  });

  const url = `${PUBLIC_BASE}${PATH}`;
  const faq = [
    { q: t("livecam_q1"), a: t("livecam_a1") },
    { q: t("livecam_q2"), a: t("livecam_a2") },
    { q: t("livecam_q3"), a: t("livecam_a3") },
  ];

  const scripts = [
    {
      id: "ld-live-breadcrumb",
      data: {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Popoyo.app", item: PUBLIC_BASE },
          { "@type": "ListItem", position: 2, name: t("livecam_h1"), item: url },
        ],
      },
    },
    {
      id: "ld-live-faq",
      data: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    },
  ];

  if (ytVideoId) {
    scripts.push({
      id: "ld-live-video",
      data: {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: t("livecam_h1"),
        description: t("livecam_intro"),
        thumbnailUrl: [
          `https://i.ytimg.com/vi/${ytVideoId}/hqdefault.jpg`,
          `https://i.ytimg.com/vi/${ytVideoId}/maxresdefault.jpg`,
        ],
        uploadDate: "2026-01-01T06:00:00-06:00",
        embedUrl: `https://www.youtube.com/embed/${ytVideoId}`,
        contentUrl: `https://www.youtube.com/watch?v=${ytVideoId}`,
        publisher: {
          "@type": "Organization",
          name: "Popoyo.app",
          logo: { "@type": "ImageObject", url: `${PUBLIC_BASE}/brand/logo.png` },
        },
        publication: {
          "@type": "BroadcastEvent",
          isLiveBroadcast: true,
          startDate: "2026-01-01T06:00:00-06:00",
        },
      },
    });
  }

  useJsonLd(scripts);

  const infoBlocks = [
    { icon: Info, h: t("livecam_about_h"), p: t("livecam_about_p") },
    { icon: MapPin, h: t("livecam_where_h"), p: t("livecam_where_p") },
    { icon: Clock, h: t("livecam_best_h"), p: t("livecam_best_p") },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[color:var(--sand)]" data-testid="live-surf-page">
      <Header alertCount={alertCount} />
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <p className="eyebrow text-[color:var(--coral)] flex items-center gap-2">
              <Radio className="w-4 h-4 animate-pulse" /> {t("live_badge")}
            </p>
            <h1
              className="text-4xl sm:text-5xl font-bold tracking-tight text-[color:var(--navy)] mt-1"
              style={{ fontFamily: "Outfit" }}
              data-testid="livecam-h1"
            >
              {t("livecam_h1")}
            </h1>
            <p className="mt-2 text-base md:text-lg text-[color:var(--ink-2)] max-w-2xl">
              {t("livecam_tagline")}
            </p>
          </div>
          <div className="shrink-0 hidden sm:block">
            <ShareButton
              path={PATH}
              title={t("livecam_h1")}
              text={t("livecam_tagline")}
              testId="share-livecam-btn"
            />
          </div>
        </div>

        <p className="text-[color:var(--ink)] leading-relaxed max-w-3xl mb-6" data-testid="livecam-intro">
          {t("livecam_intro")}
        </p>

        {/* Live player (or offline fallback) */}
        <LiveSurfReport />
        {loaded && !isLive && (
          <div
            className="clay p-8 flex flex-col items-center justify-center text-center"
            data-testid="livecam-offline"
          >
            <VideoOff className="w-10 h-10 text-[color:var(--ink-2)] mb-3" />
            <p className="font-bold text-lg" style={{ fontFamily: "Outfit" }}>{t("livecam_offline_h")}</p>
            <p className="text-sm text-[color:var(--ink-2)] mt-1 max-w-md">{t("livecam_offline_p")}</p>
          </div>
        )}

        {/* SEO content blocks */}
        <div className="grid gap-4 sm:grid-cols-3 mt-8">
          {infoBlocks.map((b, i) => {
            const Icon = b.icon;
            return (
              <section key={i} className="clay p-5" data-testid={`livecam-info-${i}`}>
                <span className="w-9 h-9 rounded-2xl bg-[color:var(--coral)]/10 text-[color:var(--coral)] flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5" />
                </span>
                <h2 className="text-lg font-bold leading-tight" style={{ fontFamily: "Outfit" }}>{b.h}</h2>
                <p className="text-sm text-[color:var(--ink-2)] mt-1.5 leading-relaxed">{b.p}</p>
              </section>
            );
          })}
        </div>

        {/* FAQ */}
        <section className="mt-10" data-testid="livecam-faq">
          <h2 className="text-2xl font-bold text-[color:var(--navy)] flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Waves className="w-6 h-6 text-[color:var(--coral)]" /> {t("livecam_faq_h")}
          </h2>
          <div className="mt-4 space-y-3">
            {faq.map((f, i) => (
              <div key={i} className="clay p-5" data-testid={`livecam-faq-${i}`}>
                <p className="font-semibold text-[color:var(--ink)]" style={{ fontFamily: "Outfit" }}>{f.q}</p>
                <p className="text-sm text-[color:var(--ink-2)] mt-1.5 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Internal link to the full surf guide */}
        <div className="mt-10 text-center">
          <Link
            to="/surf/popoyo"
            data-testid="livecam-guide-link"
            className="inline-flex items-center gap-2 pill px-6 py-3 text-sm font-bold bg-[color:var(--navy)] text-white hover:opacity-90 transition-opacity"
          >
            {t("livecam_cta")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
      <BackToTopButton />
      <Footer />
    </div>
  );
}
